import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useLoadingState } from '@common/hooks';
import { useTranslations } from '@lang';
import moment from 'moment';
import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import type { ListItemCardProps } from 'voyager';
import { SpaCategoryDetailTemplate } from 'voyager';

import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useGetSpaTreatmentsPage } from '@features/spa/hooks';
import { useGetSpaTreatments } from '@features/spa/hooks/useGetAvailableSpaTreatments';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';
import { hasAvailability } from '@utils/helpers';

import type { SpaTreatmentsCategoryV2Props } from './SpaTreatmentsCategoryV2.types';

export const SpaTreatmentsCategoryV2: FC<SpaTreatmentsCategoryV2Props> = ({
  property,
  journeyParams,
  language,
  category,
}) => {
  const router = useRouter();
  const { t } = useTranslations();

  const isUncategorized = String(category.id) === UNCATEGORIZED_VALUE;
  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const { spaTreatments = [], isFetching } = useGetSpaTreatments(
    property.id,
    language,
    selectedDateString,
    isUncategorized ? null : Number(category.id)
  );

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, spaTreatments.length > 0);

  const { data: treatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const fallbackImageUrl = treatmentsPage?.heroBackgroundImage?.url;

  const categoryName = isUncategorized ? t('spa.noCategory') : category.name;

  const filteredTreatments = isUncategorized
    ? spaTreatments.filter((tr) => !tr.categories || tr.categories.length === 0)
    : spaTreatments;

  const items: ListItemCardProps[] = filteredTreatments
    .sort((a, b) => a.priority - b.priority)
    .map((tr) => {
      const isAvailable = hasAvailability(tr, selectedDateString);
      return {
        id: String(tr.id),
        name: tr.name,
        image: {
          src: tr.image?.url || fallbackImageUrl,
          alt: tr.name,
        },
        price:
          tr.price?.amount != null
            ? {
                amount: tr.price.amount,
                currencyCode: tr.price.currencyCode,
                label: t('common.from'),
              }
            : undefined,
        unavailable: !isFetching && !isAvailable,
      };
    });

  const handleSelectItem = useCallback(
    (id: string) => {
      router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
    },
    [router]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const journeyBar = (
    <JourneyPickerV2Connected
      dateMode="single"
      dateContext="spa"
      showGuests
      numberOfMonths={1}
      categories={navCategories}
      category="spa-treatments"
      onCategoryChange={handleNavCategoryChange}
    />
  );

  return (
    <>
      <Spinner showDelayMessage isActive={isRefetching} />
      <SpaCategoryDetailTemplate
        categoryName={categoryName}
        categoryDescription={isUncategorized ? undefined : category.description}
        categoryImageUrl={
          isUncategorized ? fallbackImageUrl : category.image?.url || fallbackImageUrl
        }
        items={items}
        isLoading={isInitialLoading}
        onSelectItem={handleSelectItem}
        onEditSearch={handleEditSearch}
        journeyBar={journeyBar}
        labels={{
          buttonLabel: t('common.moreDetails'),
          unavailableLabel: t('common.unavailable'),
          editSearchLabel: t('common.editSearch'),
        }}
      />
    </>
  );
};
